/**
 * MediaCanvasLayout -- Immersive cinema canvas for the Media Portal.
 *
 * Reclaims ALL AppShell padding for edge-to-edge immersion.
 * overflow-clip + min-w-0 prevent negative-margin expansion from
 * leaking into the flex parent chain and causing page-level overflow.
 * Uses overflow-clip (not overflow-hidden) to avoid creating a scroll
 * container that would break sticky positioning in child pages.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useOutletContext } from "react-router-dom";

import { useBleedLayout } from "../../../../contexts/LayoutContext";
import { useMe } from "../../../../hooks/useMe";
import { useWSDSettings } from "../../../../hooks/useWSDSettings";

import { Card } from "../../../ui/Card";
import { Icon } from "../../../ui/Icon";

// ---------------------------------------------------------------------------
// Dev-only overflow sentinel
// ---------------------------------------------------------------------------

function useCanvasDiagnostics(canvasRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const raf = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const prefix = "[WSD Canvas Diagnostics]";
      for (const [sel, label] of [
        ["[data-canvas-hero]", "hero"],
        ["[data-canvas-shelf]", "first shelf"],
        ["[data-canvas-card]", "first card"],
      ] as const) {
        const el = canvas.querySelector(sel);
        if (el && el.scrollWidth > el.clientWidth) {
          console.warn(`${prefix} Overflow on "${label}": scrollW=${el.scrollWidth}, clientW=${el.clientWidth}`);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [canvasRef]);
}

// ---------------------------------------------------------------------------
// Canvas outlet context — lets child pages render content beside the scroll
// area (e.g., AlphabetRail) without being inside the scroll container.
// ---------------------------------------------------------------------------

export type CanvasOutletContext = {
  /** Portal target for side-rail content (beside scroll area, not inside it). */
  sideRailEl: HTMLDivElement | null;
  /** The canvas scroll container element. */
  canvasEl: HTMLDivElement | null;
};

/** Hook for child pages to access the canvas layout context. */
export function useCanvasContext(): CanvasOutletContext {
  return useOutletContext<CanvasOutletContext>();
}

// ---------------------------------------------------------------------------
// MediaCanvasLayout
// ---------------------------------------------------------------------------

export const MediaCanvasLayout = () => {
  useBleedLayout();

  const { appConfig } = useMe();
  const featureEnabled = appConfig?.features?.wsdashboardEnabled ?? false;
  const { settings, loading: settingsLoading } = useWSDSettings();
  const serviceConfigured = settings?.embyConfigured || settings?.jellyfinConfigured || settings?.plexConfigured;
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [sideRailEl, setSideRailEl] = useState<HTMLDivElement | null>(null);
  useCanvasDiagnostics(canvasRef);

  // Feature flag gate
  if (!featureEnabled) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <Link to="/streaming/dashboard/control-center" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Icon icon="lucide:chevron-left" className="h-4 w-4" aria-hidden />
            Control Center
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Media Portal</h1>
        </div>
        <Card title="Feature Not Enabled" description="The Media Portal requires WSDashboard to be enabled.">
          <p className="text-sm text-muted-foreground">
            Enable WSDashboard in <strong>General Settings &rarr; Feature Flags</strong> to access the Media Portal.
          </p>
        </Card>
      </div>
    );
  }

  // Loading state — show skeleton while WSD settings load
  if (settingsLoading) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <Link to="/streaming/dashboard/control-center" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Icon icon="lucide:chevron-left" className="h-4 w-4" aria-hidden />
            Control Center
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Media Portal</h1>
        </div>
        <div className="animate-pulse space-y-6">
          <div className="aspect-[21/9] max-h-[400px] rounded-xl bg-muted/10" />
          <div className="space-y-4 px-4">
            <div className="h-5 w-40 rounded bg-muted" />
            <div className="flex gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-[240px]">
                  <div className="aspect-video rounded-lg bg-muted/10" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!serviceConfigured) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <Link to="/streaming/dashboard/control-center" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Icon icon="lucide:chevron-left" className="h-4 w-4" aria-hidden />
            Control Center
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Media Portal</h1>
        </div>
        <Card>
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <div className="rounded-full bg-muted/30 p-5">
              <Icon icon="lucide:library" className="h-12 w-12 text-muted-foreground/40" aria-hidden />
            </div>
            <h3 className="text-lg font-semibold">Configure Your Media Servers</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Connect your Emby, Jellyfin, or Plex server in the Streaming Dashboard settings.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Immersive cinema canvas
  //
  // The flex row allows child pages to render a side-rail (e.g., AlphabetRail)
  // OUTSIDE the scroll container via portal into sideRailEl. This sidesteps
  // CSS sticky limitations inside nested overflow contexts.
  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-clip min-w-0">
      <div className="flex flex-1 min-h-0">
        <div
          ref={canvasRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative qb-scroll"
          role="region"
          aria-label="Media Portal"
        >
          <Outlet context={{ sideRailEl, canvasEl: canvasRef.current } satisfies CanvasOutletContext} />
        </div>
        {/* Side-rail slot — sits outside the scroll area so it doesn't scroll
            with page content. sticky + self-start keeps it pinned below the
            TopBar if the window ends up being the actual scroll target. */}
        <div
          ref={setSideRailEl}
          className="hidden lg:block shrink-0 sticky top-[calc(var(--topbar-height,72px)+1rem)] self-start"
        />
      </div>
    </div>
  );
};
